import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { CreateChannelRequest, } from '@wit-support/admin-contracts';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, toast, } from '@wit-support/ui';
import { Actions, SubmitButton, ZodField, ZodForm } from '@wit-support/ui-forms';
import { createChannel, startGoogleOAuth } from '../api.js';
import { useGoogleOAuthPopup } from '../hooks/use-google-oauth-popup.js';
// Minimal schema for the Gmail mode — only the display name. Everything else
// (clientId/secret/refreshToken/email) is filled server-side by the OAuth
// callback once Google redirects back with a code.
const GmailStartSchema = z.object({
    name: z
        .string()
        .min(1, 'Required')
        .max(128, 'Must be 128 characters or fewer')
        .meta({ label: 'Name' }),
});
export function CreateChannelDialog({ onClose, onCreated }) {
    const [kind, setKind] = useState(null);
    return (_jsx(Dialog, { open: true, onOpenChange: (v) => (!v ? onClose() : null), children: _jsx(DialogContent, { children: kind === null ? (_jsx(KindPicker, { onPick: setKind, onCancel: onClose })) : kind === 'gmail' ? (_jsx(GmailConnectMode, { onBack: () => setKind(null), onCancel: onClose, onConnected: onCreated })) : (_jsx(WebFormMode, { onBack: () => setKind(null), onCancel: onClose, onCreated: onCreated })) }) }));
}
function KindPicker({ onPick, onCancel }) {
    return (_jsxs(_Fragment, { children: [_jsx(DialogTitle, { children: "Add channel" }), _jsx(DialogDescription, { children: "Pick a channel type to continue." }), _jsxs("div", { className: "grid grid-cols-1 gap-2 sm:grid-cols-2", children: [_jsxs(Button, { variant: "secondary", className: "h-auto flex-col items-start gap-1 p-4 text-left", onClick: () => onPick('gmail'), children: [_jsx("span", { className: "text-sm font-semibold text-[var(--color-text-primary)]", children: "Gmail" }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "Sign in with Google \u2014 no secrets to paste." })] }), _jsxs(Button, { variant: "secondary", className: "h-auto flex-col items-start gap-1 p-4 text-left", onClick: () => onPick('web_form'), children: [_jsx("span", { className: "text-sm font-semibold text-[var(--color-text-primary)]", children: "Web form" }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "Receive messages from a website contact form via signed webhook." })] })] }), _jsx(Actions, { children: _jsx(Button, { type: "button", variant: "ghost", onClick: onCancel, children: "Cancel" }) })] }));
}
function GmailConnectMode({ onBack, onCancel, onConnected, }) {
    // Two signals end the flow: the callback HTML posts {kind:'google-oauth'}
    // to its opener (viaMessage=true — show the success toast), and separately
    // the popup closes (user dismissed or it auto-closed after success) —
    // refetch regardless, but without a toast since we don't know it succeeded.
    const { popup, openPopup } = useGoogleOAuthPopup({
        onDone: (viaMessage) => {
            if (viaMessage)
                toast.success('Gmail channel connected');
            onConnected();
        },
    });
    const mut = useMutation({
        mutationFn: (body) => startGoogleOAuth(body),
        onSuccess: ({ authUrl }) => openPopup(authUrl),
        onError: (err) => {
            const msg = err.message;
            if (msg.includes('503')) {
                toast.error('Google OAuth is not configured on this server.');
            }
            else {
                toast.error(msg);
            }
        },
    });
    return (_jsxs(_Fragment, { children: [_jsx(DialogTitle, { children: "Connect Gmail" }), _jsx(DialogDescription, { children: "Give this channel a name, then sign in with the Google account you want to receive messages from. You can add as many Gmail accounts as you like." }), _jsxs(ZodForm, { schema: GmailStartSchema, defaultValues: { name: '' }, onSubmit: (values) => mut.mutate(values), children: [_jsx(ZodField, { name: "name" }), _jsxs(Actions, { children: [_jsx(Button, { type: "button", variant: "ghost", onClick: onBack, children: "Back" }), _jsx(Button, { type: "button", variant: "ghost", onClick: onCancel, children: "Cancel" }), _jsx(SubmitButton, { pendingLabel: "Opening Google\u2026", children: popup && !popup.closed ? 'Waiting for Google…' : 'Sign in with Google' })] })] })] }));
}
function WebFormMode({ onBack, onCancel, onCreated, }) {
    const mut = useMutation({
        mutationFn: (body) => createChannel(body),
        onSuccess: () => {
            toast.success('Channel created');
            onCreated();
        },
        onError: (err) => toast.error(err.message),
    });
    return (_jsxs(_Fragment, { children: [_jsx(DialogTitle, { children: "Add web form channel" }), _jsx(DialogDescription, { children: "Receive inbound messages from a website contact form via signed webhook." }), _jsxs(ZodForm, { schema: CreateChannelRequest, defaultValues: {
                    name: '',
                    outboundIdentity: '',
                    status: 'active',
                    config: {
                        kind: 'web_form',
                        recipientHint: '',
                        webhookSecret: '',
                        allowAttachments: false,
                    },
                }, onSubmit: (values) => mut.mutate(values), children: [_jsx(ZodField, { name: "name" }), _jsx(ZodField, { name: "outboundIdentity" }), _jsx(ZodField, { name: "status" }), _jsx(ZodField, { name: "config" }), _jsxs(Actions, { children: [_jsx(Button, { type: "button", variant: "ghost", onClick: onBack, children: "Back" }), _jsx(Button, { type: "button", variant: "ghost", onClick: onCancel, children: "Cancel" }), _jsx(SubmitButton, { pendingLabel: "Creating\u2026", children: "Create" })] })] })] }));
}
//# sourceMappingURL=create-dialog.js.map